<script lang="ts">
import type { NxRecipient } from './recipients';

function defaultInvalidMessage(invalid: readonly NxRecipient[]): string {
  return invalid.length === 1
    ? `Ungültige Adresse: ${invalid[0]!.email}`
    : `${invalid.length} ungültige Adressen`;
}

function isThenable<T>(value: unknown): value is PromiseLike<T> {
  return typeof (value as PromiseLike<T> | null)?.then === 'function';
}
</script>

<script setup lang="ts">
/**
 * A field for several e-mail addresses — To/Cc/Bcc of a message, the
 * attendees of an event, the people a calendar is shared with, the „send to“
 * of any dialog. Each address is a chip; the text input sits after the chips.
 *
 * - Typing: `,` `;` Enter (and Tab, which then moves on as usual) turn the
 *   typed text into chips. Pasted lists are split at `,` `;` line breaks and
 *   spaces between addresses. `Name <addr@x.de>` keeps the name.
 * - Suggestions: `search(query, signal)` is the caller's directory lookup
 *   (sync or promise), asked `debounce` ms after the last keystroke; a newer
 *   query aborts the older one. Already chosen addresses are never offered.
 * - Invalid addresses stay as chips, marked in the danger colour, with a
 *   message below the field; clicking such a chip puts it back into the input
 *   to fix it. With `allow-invalid` off they are refused instead: the text
 *   stays in the input. `isValidEmail` replaces the default rule (e.g. to allow
 *   addresses without a dot in the domain).
 * - `v-model` is the list of `{ email, name? }`; `v-model:query` (optional)
 *   exposes the text still being typed, and `commit()` turns it into chips.
 *   Leaving the field commits it too.
 * - `bare` drops the field chrome for rows that draw their own lines (a
 *   composer head).
 *
 * Keyboard (combobox + listbox, focus stays in the input): ↓/↑ move through
 * the suggestions, Enter/Tab take the highlighted one, Escape closes the list
 * and then clears the text, Backspace in the empty input removes the last chip.
 * Additions and removals are announced in a polite live region.
 */
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  ref,
  shallowRef,
  useAttrs,
  useId,
  watch,
  type StyleValue,
} from 'vue';
import NxAvatar from '../Avatar/NxAvatar.vue';
import NxChip from '../Chip/NxChip.vue';
import NxDropdown from '../Dropdown/NxDropdown.vue';
import NxTagField from '../Inputs/NxTagField.vue';
import { labelInjectionKey } from '../Inputs/internal';
import {
  addRecipients,
  formatRecipient,
  isValidRecipientEmail,
  parseRecipients,
  recipientKey,
  recipientLabel,
  splitRecipientText,
  takeCompleteRecipients,
  type NxRecipientSearch,
  type NxRecipientSuggestion,
} from './recipients';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    /** The directory lookup. Without it the field takes typed addresses only. */
    search?: NxRecipientSearch;
    /** The address rule; defaults to the kit's e-mail check. */
    isValidEmail?: (email: string) => boolean;
    /**
     * Keep invalid addresses as (marked) chips. Off: they stay in the input as
     * text, with the message below, until they are fixed.
     */
    allowInvalid?: boolean;
    /** Pause after the last keystroke before `search` is asked (ms); `0` asks at once. */
    debounce?: number;
    /** Characters needed before suggestions are searched. */
    minQueryLength?: number;
    /** Suggestions shown at most. */
    maxSuggestions?: number;
    /** No field chrome: chips and input sit in a plain row. */
    bare?: boolean;
    /** Scroll cap of the chip area (em, follows the density scale). */
    maxHeight?: string;
    /** At least one address is needed. */
    required?: boolean;
    /** Force the invalid look (e.g. a rejection from the server). */
    invalid?: boolean;
    /** Fixed addresses: shown, not editable. */
    readonly?: boolean;
    disabled?: boolean;
    /** Id of the text input; generated (and tied to a surrounding label) when omitted. */
    id?: string;
    /** Accessible name when no visible label names the field. */
    ariaLabel?: string;
    placeholder?: string;
    /** Placeholder once addresses are chosen. */
    placeholderMore?: string;
    /** Shown instead of chips when nothing is chosen and the field is read-only. */
    emptyText?: string;
    noResultsLabel?: string;
    searchingLabel?: string;
    /** Accessible name of a chip's ×. */
    removeLabel?: (label: string) => string;
    /** Title of an invalid chip (a click puts it back into the input). */
    editLabel?: (label: string) => string;
    /** Message below the field while invalid addresses are in it. */
    invalidMessage?: (invalid: readonly NxRecipient[]) => string;
    requiredMessage?: string;
    /** Live-region announcement after an address was added. */
    addedLabel?: (label: string) => string;
    /** Live-region announcement after an address was removed. */
    removedLabel?: (label: string) => string;
  }>(),
  {
    search: undefined,
    isValidEmail: isValidRecipientEmail,
    allowInvalid: true,
    debounce: 150,
    minQueryLength: 1,
    maxSuggestions: 8,
    bare: false,
    maxHeight: '7.5em',
    required: false,
    invalid: false,
    readonly: false,
    disabled: false,
    id: undefined,
    ariaLabel: undefined,
    placeholder: 'name@firma.de',
    placeholderMore: '',
    emptyText: 'Kein Empfänger',
    noResultsLabel: 'Kein Treffer',
    searchingLabel: 'Suche …',
    removeLabel: (label: string) => `${label} entfernen`,
    editLabel: (label: string) => `${label} – zum Bearbeiten klicken`,
    invalidMessage: defaultInvalidMessage,
    requiredMessage: 'Bitte mindestens einen Empfänger angeben',
    addedLabel: (label: string) => `${label} hinzugefügt`,
    removedLabel: (label: string) => `${label} entfernt`,
  }
);

const model = defineModel<NxRecipient[]>({ required: true });
/** The text still being typed (not yet a chip). */
const queryModel = defineModel<string>('query', { default: '' });
// A local copy: a bound model only reflects a write after the parent
// re-rendered, and the search must see the new text at once.
const query = ref(queryModel.value);
watch(queryModel, (value) => {
  if (value !== query.value) query.value = value;
});
function setQuery(value: string) {
  query.value = value;
  if (queryModel.value !== value) queryModel.value = value;
}

const emit = defineEmits<{
  /** Addresses were added (typed, pasted or picked). */
  add: [recipients: NxRecipient[]];
  /** An address was removed (×, Backspace, or taken back into the input). */
  remove: [recipient: NxRecipient];
}>();

defineSlots<{
  /** One suggestion row; defaults to avatar, name, address and `sublabel`. */
  suggestion?(props: { suggestion: NxRecipientSuggestion; active: boolean }): unknown;
}>();

// class/style stay on the root (layout); everything else — name, autofocus,
// data-*, listeners — lands on the text input.
const attrs = useAttrs();
const rootAttrs = computed(() => ({ class: attrs.class, style: attrs.style as StyleValue }));
const inputAttrs = computed(() =>
  Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== 'class' && key !== 'style'))
);

const inputId = props.id ?? useId();
const listboxId = `${inputId}-listbox`;
const messageId = `${inputId}-message`;

const labelInjection = inject(labelInjectionKey, null);
if (labelInjection && !labelInjection.for.value) labelInjection.setFor(inputId);

const rootRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const popupRef = ref<HTMLElement | null>(null);

const interactive = computed(() => !props.readonly && !props.disabled);

// ── Validation ──────────────────────────────────────────────────────────────
const touched = ref(false);
const invalidRecipients = computed(() => model.value.filter((r) => !props.isValidEmail(r.email)));
/** Addresses refused (allow-invalid off) and put back into the input. */
const rejected = ref<NxRecipient[]>([]);
const missing = computed(() => props.required && model.value.length === 0);
const showRequiredError = computed(() => missing.value && (touched.value || props.invalid));
const message = computed(() => {
  const invalid = [...invalidRecipients.value, ...rejected.value];
  if (invalid.length > 0) return props.invalidMessage(invalid);
  return showRequiredError.value ? props.requiredMessage : '';
});
const isInvalid = computed(
  () =>
    props.invalid ||
    showRequiredError.value ||
    invalidRecipients.value.length > 0 ||
    rejected.value.length > 0
);

function isRecipientInvalid(recipient: NxRecipient): boolean {
  return !props.isValidEmail(recipient.email);
}

// ── Live region ─────────────────────────────────────────────────────────────
const announcement = ref('');

function announce(text: string) {
  // Cleared first so the same sentence twice in a row is announced twice.
  announcement.value = '';
  void nextTick(() => {
    announcement.value = text;
  });
}

// ── Changing the list ───────────────────────────────────────────────────────
/**
 * Add what is valid (or everything, with allow-invalid). Returns the text of
 * the refused addresses, which the caller keeps in the input.
 */
function addMany(additions: readonly NxRecipient[]): string {
  if (!interactive.value || additions.length === 0) return '';
  let accepted = additions;
  if (!props.allowInvalid) {
    rejected.value = additions.filter((r) => !props.isValidEmail(r.email));
    accepted = additions.filter((r) => props.isValidEmail(r.email));
  }
  const refused = rejected.value.map(formatRecipient).join(', ');
  const before = model.value;
  const next = addRecipients(before, accepted);
  if (next === before) return refused;
  const known = new Set(before.map(recipientKey));
  const added = next.filter((entry) => !known.has(recipientKey(entry)));
  model.value = next;
  if (added.length > 0) {
    emit('add', added);
    announce(added.map((entry) => props.addedLabel(recipientLabel(entry))).join(', '));
  }
  return refused;
}

function removeAt(index: number, refocus: boolean) {
  if (!interactive.value) return;
  const recipient = model.value[index];
  if (!recipient) return;
  model.value = model.value.filter((_, i) => i !== index);
  emit('remove', recipient);
  announce(props.removedLabel(recipientLabel(recipient)));
  if (refocus) void nextTick(() => inputRef.value?.focus());
}

/** An invalid chip goes back into the input to be fixed. */
function editAt(index: number) {
  const recipient = model.value[index];
  if (!recipient || !interactive.value || !isRecipientInvalid(recipient)) return;
  commit();
  removeAt(index, false);
  setQuery(formatRecipient(recipient));
  void nextTick(() => {
    const input = inputRef.value;
    input?.focus();
    input?.setSelectionRange(input.value.length, input.value.length);
  });
}

/** Turn the typed text into chips. Returns the list afterwards. */
function commit(): NxRecipient[] {
  const text = query.value;
  if (text.trim()) {
    resetQuery();
    const refused = addMany(parseRecipients(text));
    if (refused) setQuery(refused);
  }
  return model.value;
}

// ── Suggestions ─────────────────────────────────────────────────────────────
/** The user wants the list: set by typing / ↓, cleared by Escape, a pick, leaving. */
const open = ref(false);
/** A search for the current query is scheduled or on its way. */
const loading = ref(false);
const results = shallowRef<readonly NxRecipientSuggestion[] | null>(null);
let resultsQuery = '';
const activeIndex = ref(-1);

let searchTimer: ReturnType<typeof setTimeout> | undefined;
let searchAbort: AbortController | null = null;
let searchSeq = 0;

const options = computed<NxRecipientSuggestion[]>(() => {
  if (!results.value) return [];
  const taken = new Set(model.value.map(recipientKey));
  const seen = new Set<string>();
  const list: NxRecipientSuggestion[] = [];
  for (const hit of results.value) {
    const key = recipientKey(hit);
    if (!key || taken.has(key) || seen.has(key)) continue;
    seen.add(key);
    list.push(hit);
    if (list.length >= props.maxSuggestions) break;
  }
  return list;
});

const popupVisible = computed(
  () => interactive.value && open.value && (loading.value || results.value !== null)
);
const listExpanded = computed(() => popupVisible.value && options.value.length > 0);

function optionId(index: number): string {
  return `${listboxId}-option-${index}`;
}

const activeOptionId = computed(() =>
  listExpanded.value && activeIndex.value >= 0 && activeIndex.value < options.value.length
    ? optionId(activeIndex.value)
    : undefined
);

watch(
  () => options.value.length,
  (count) => {
    if (activeIndex.value >= count) activeIndex.value = count - 1;
  }
);

watch(activeIndex, () => {
  void nextTick(() => {
    popupRef.value?.querySelector('.is-active')?.scrollIntoView?.({ block: 'nearest' });
  });
});

function cancelSearch() {
  if (searchTimer !== undefined) clearTimeout(searchTimer);
  searchTimer = undefined;
  searchAbort?.abort();
  searchAbort = null;
  searchSeq += 1;
}

function deliver(q: string, hits: readonly NxRecipientSuggestion[]) {
  results.value = hits;
  resultsQuery = q;
  loading.value = false;
  activeIndex.value = options.value.length > 0 ? 0 : -1;
}

function runSearch(q: string) {
  const token = ++searchSeq;
  const controller = new AbortController();
  searchAbort = controller;
  const settle = (hits: readonly NxRecipientSuggestion[] | null | undefined, error?: unknown) => {
    // A newer query (or leaving the field) has taken over: drop the answer.
    if (token !== searchSeq || controller.signal.aborted) return;
    searchAbort = null;
    if (error !== undefined) console.warn('[NxRecipientInput] search failed', error);
    deliver(q, Array.isArray(hits) ? hits : []);
  };
  if (!props.search) {
    settle([]);
    return;
  }
  try {
    const answer = props.search(q, controller.signal);
    if (isThenable<readonly NxRecipientSuggestion[]>(answer)) {
      answer.then(
        (hits) => settle(hits),
        (error: unknown) => settle([], error ?? 'rejected')
      );
    } else {
      settle(answer);
    }
  } catch (error) {
    settle([], error);
  }
}

function scheduleSearch() {
  cancelSearch();
  activeIndex.value = -1;
  const q = query.value.trim();
  if (!props.search || q.length < props.minQueryLength) {
    results.value = null;
    resultsQuery = '';
    loading.value = false;
    open.value = false;
    return;
  }
  open.value = true;
  loading.value = true;
  if (props.debounce > 0) {
    searchTimer = setTimeout(() => {
      searchTimer = undefined;
      runSearch(q);
    }, props.debounce);
  } else {
    runSearch(q);
  }
}

function openSuggestions() {
  const q = query.value.trim();
  if (!interactive.value || !props.search || q.length < props.minQueryLength) return;
  if (results.value && resultsQuery === q) {
    open.value = true;
    activeIndex.value = options.value.length > 0 ? 0 : -1;
    return;
  }
  scheduleSearch();
}

function closePopup() {
  open.value = false;
  activeIndex.value = -1;
  if (loading.value) {
    cancelSearch();
    loading.value = false;
  }
}

function resetQuery() {
  cancelSearch();
  setQuery('');
  results.value = null;
  resultsQuery = '';
  loading.value = false;
  open.value = false;
  activeIndex.value = -1;
}

function pick(suggestion: NxRecipientSuggestion) {
  if (!interactive.value) return;
  resetQuery();
  const refused = addMany(
    suggestion.members ?? [
      suggestion.name ? { email: suggestion.email, name: suggestion.name } : suggestion,
    ]
  );
  if (refused) setQuery(refused);
  inputRef.value?.focus();
}

// ── Events ──────────────────────────────────────────────────────────────────
function onInput(event: Event) {
  const input = event.target as HTMLInputElement;
  const { complete, rest } = takeCompleteRecipients(input.value);
  rejected.value = [];
  const refused = complete.length > 0 ? addMany(complete) : '';
  const text = refused ? [refused, rest].filter(Boolean).join(', ') : rest;
  setQuery(text);
  // A lone separator changes no state: pull the DOM back to the model.
  if (input.value !== text) input.value = text;
  scheduleSearch();
}

function onPaste(event: ClipboardEvent) {
  const pasted = event.clipboardData?.getData('text') ?? '';
  // One plain address is ordinary typing; a list becomes chips at once.
  if (splitRecipientText(pasted).length < 2 && parseRecipients(pasted).length < 2) return;
  event.preventDefault();
  const input = event.target as HTMLInputElement;
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? input.value.length;
  const text = `${input.value.slice(0, start)}${pasted}${input.value.slice(end)}`;
  resetQuery();
  const refused = addMany(parseRecipients(text));
  setQuery(refused);
  input.value = refused;
}

function moveActive(step: 1 | -1) {
  const count = options.value.length;
  if (count === 0) return;
  const current = activeIndex.value;
  activeIndex.value = current < 0 ? (step > 0 ? 0 : count - 1) : (current + step + count) % count;
}

function activeOption(): NxRecipientSuggestion | undefined {
  return listExpanded.value ? options.value[activeIndex.value] : undefined;
}

function onKeydown(event: KeyboardEvent) {
  if (event.isComposing || event.keyCode === 229) return;
  switch (event.key) {
    case 'ArrowDown':
    case 'ArrowUp':
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (!popupVisible.value && !props.search) return;
      event.preventDefault();
      if (popupVisible.value) moveActive(event.key === 'ArrowDown' ? 1 : -1);
      else openSuggestions();
      return;
    case 'Enter': {
      // Cmd/Ctrl+Enter belongs to the surrounding form (send).
      if (event.metaKey || event.ctrlKey) return;
      const option = activeOption();
      if (option) {
        event.preventDefault();
        pick(option);
      } else if (query.value.trim()) {
        event.preventDefault();
        commit();
      }
      return;
    }
    case 'Tab': {
      // Never prevented: the chip is made, focus moves on as usual.
      if (event.shiftKey) return;
      const option = activeOption();
      if (option) pick(option);
      else commit();
      return;
    }
    case 'Escape':
      // First the list, then the text; with neither, Escape belongs to the dialog.
      if (popupVisible.value) closePopup();
      else if (query.value) resetQuery();
      else return;
      event.preventDefault();
      event.stopPropagation();
      return;
    case 'Backspace':
      if (query.value === '' && model.value.length > 0) {
        event.preventDefault();
        removeAt(model.value.length - 1, false);
      }
      return;
  }
}

/** Focus left the whole field: the typed text becomes chips, validation starts. */
function onFocusout(event: FocusEvent) {
  const next = event.relatedTarget;
  if (next instanceof Node && rootRef.value?.contains(next)) return;
  touched.value = true;
  closePopup();
  commit();
}

watch(
  () => props.readonly || props.disabled,
  (off) => {
    if (off) closePopup();
  }
);

onBeforeUnmount(cancelSearch);

const inputPlaceholder = computed(() =>
  model.value.length > 0 ? props.placeholderMore : props.placeholder
);

function focus() {
  inputRef.value?.focus();
}

/** Show the messages now (e.g. on submit) and tell whether the field is valid. */
function validate(): boolean {
  touched.value = true;
  return !missing.value && !props.invalid && invalidRecipients.value.length === 0;
}

defineExpose({ focus, commit, validate, invalidRecipients });
</script>

<template>
  <div
    ref="rootRef"
    class="nx-recipient-input"
    :class="{
      'is-bare': bare,
      'is-readonly': readonly,
      'is-disabled': disabled,
      'is-invalid': isInvalid,
    }"
    v-bind="rootAttrs"
    data-testid="nx-recipient-input"
    @focusout="onFocusout"
  >
    <NxDropdown :is-open="popupVisible" @close="closePopup">
      <template #trigger>
        <component
          :is="bare ? 'div' : NxTagField"
          v-bind="bare ? {} : { maxHeight }"
          class="nx-recipient-input-field"
          :style="bare ? { maxHeight } : undefined"
        >
          <ul v-if="model.length > 0" class="nx-recipient-input-chips" role="list">
            <li
              v-for="(recipient, index) in model"
              :key="recipientKey(recipient)"
              class="nx-recipient-input-item"
            >
              <NxChip
                class="nx-recipient-input-chip"
                :class="{ 'is-invalid': isRecipientInvalid(recipient) }"
                size="xs"
                :static="readonly"
                :removable="!readonly"
                :disabled="disabled"
                :remove-label="removeLabel(recipientLabel(recipient))"
                :title="
                  isRecipientInvalid(recipient)
                    ? editLabel(formatRecipient(recipient))
                    : formatRecipient(recipient)
                "
                :data-invalid="isRecipientInvalid(recipient) || undefined"
                @click="editAt(index)"
                @remove="removeAt(index, true)"
              >
                {{ recipientLabel(recipient) }}
              </NxChip>
            </li>
          </ul>
          <input
            v-if="!readonly"
            v-bind="inputAttrs"
            :id="inputId"
            ref="inputRef"
            class="nx-recipient-input-input"
            type="text"
            inputmode="email"
            role="combobox"
            autocomplete="off"
            aria-autocomplete="list"
            :aria-expanded="listExpanded"
            :aria-controls="listExpanded ? listboxId : undefined"
            :aria-activedescendant="activeOptionId"
            :aria-label="ariaLabel"
            :aria-required="required || undefined"
            :aria-invalid="isInvalid || undefined"
            :aria-describedby="message ? messageId : undefined"
            :placeholder="inputPlaceholder"
            :disabled="disabled"
            :value="query"
            @input="onInput"
            @paste="onPaste"
            @keydown="onKeydown"
          />
          <span v-else-if="model.length === 0" class="nx-recipient-input-empty">
            {{ emptyText }}
          </span>
        </component>
      </template>

      <!-- mousedown.prevent: picking must not move the focus out of the input. -->
      <div ref="popupRef" class="nx-recipient-input-popup" @mousedown.prevent>
        <div
          v-if="options.length > 0"
          :id="listboxId"
          role="listbox"
          class="nx-recipient-input-listbox"
          :class="{ 'is-stale': loading }"
          :aria-label="ariaLabel"
          :aria-busy="loading || undefined"
        >
          <div
            v-for="(suggestion, index) in options"
            :id="optionId(index)"
            :key="recipientKey(suggestion)"
            role="option"
            class="nx-recipient-input-option"
            :class="{ 'is-active': index === activeIndex }"
            :aria-selected="index === activeIndex"
            @pointerenter="activeIndex = index"
            @click="pick(suggestion)"
          >
            <slot name="suggestion" :suggestion="suggestion" :active="index === activeIndex">
              <NxAvatar :name="suggestion.name || suggestion.email" :size="24" aria-hidden="true" />
              <span class="nx-recipient-input-option-text">
                <span class="nx-recipient-input-option-label">
                  {{ suggestion.name || suggestion.email }}
                </span>
                <span
                  v-if="suggestion.name && !suggestion.members"
                  class="nx-recipient-input-option-email"
                >
                  {{ suggestion.email }}
                </span>
              </span>
              <span v-if="suggestion.sublabel" class="nx-recipient-input-option-aside">
                {{ suggestion.sublabel }}
              </span>
            </slot>
          </div>
        </div>
        <div v-else class="nx-recipient-input-message">
          {{ loading ? searchingLabel : noResultsLabel }}
        </div>
      </div>
    </NxDropdown>

    <p v-if="message" :id="messageId" class="nx-recipient-input-error" role="alert">
      {{ message }}
    </p>

    <span class="nx-recipient-input-live" aria-live="polite">{{ announcement }}</span>
  </div>
</template>

<style scoped>
.nx-recipient-input {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}

/* Bare: a plain wrapping row, the host draws the lines. */
.nx-recipient-input.is-bare .nx-recipient-input-field {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  min-height: var(--nxt-control-size-sm);
  overflow-y: auto;
  font-size: var(--nxt-font-size-sm);
}

/* The chips flow inline with the input: the list draws no box, its items are
   flex items of the field (list semantics stay via role="list"). */
.nx-recipient-input-chips {
  display: contents;
  list-style: none;
  margin: 0;
  padding: 0;
  /* The global type reset sizes every bare <ul> at 16px medium. */
  font-size: inherit;
  font-weight: var(--nxt-font-weight-regular);
}
.nx-recipient-input-item {
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
}

/* Invalid address: the chip's own tokens re-pointed at the danger role. */
.nx-recipient-input-chip.is-invalid {
  --nxt-chip-bg: var(--nxt-danger-subtle-bg);
  --nxt-chip-bg-hover: var(--nxt-danger-subtle-bg-hover);
  --nxt-chip-border: var(--nxt-danger-border);
  --nxt-chip-fg: var(--nxt-danger-subtle-fg);
}

.nx-recipient-input-input {
  flex: 1 1 calc(var(--nxt-size-unit) * 8);
  min-width: calc(var(--nxt-size-unit) * 5);
  padding: 0 var(--nxt-space-2xs);
  border: none;
  /* The field around it draws the focus ring (NxTagField :has(input:focus-visible)). */
  outline: none;
  background: transparent;
  font: inherit;
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-input-fg);
}
.nx-recipient-input-input::placeholder {
  color: var(--nxt-input-placeholder);
}
.nx-recipient-input-input:disabled {
  cursor: not-allowed;
  color: var(--nxt-input-fg-disabled);
}
/* Bare rows have no field ring: the input shows its own. */
.nx-recipient-input.is-bare .nx-recipient-input-input:focus-visible {
  border-radius: var(--nxt-radius-sm);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-input-ring);
}

.nx-recipient-input-empty {
  padding-inline: var(--nxt-space-2xs);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-subtle);
}

/* Field states on NxTagField's chrome. */
.nx-recipient-input.is-invalid:not(.is-bare) .nx-recipient-input-field {
  border-color: var(--nxt-input-border-invalid);
}
.nx-recipient-input.is-invalid:not(.is-bare) .nx-recipient-input-field:has(input:focus-visible) {
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-input-ring-invalid);
}
.nx-recipient-input.is-disabled:not(.is-bare) .nx-recipient-input-field {
  background: var(--nxt-input-bg-disabled);
  cursor: not-allowed;
}
.nx-recipient-input.is-disabled .nx-recipient-input-field:hover,
.nx-recipient-input.is-readonly .nx-recipient-input-field:hover {
  border-color: var(--nxt-input-border);
}
.nx-recipient-input.is-readonly:not(.is-bare) .nx-recipient-input-field {
  background: transparent;
}

/* Suggestions: directly under the field, about seven rows, then it scrolls. */
.nx-recipient-input-popup {
  max-height: calc(var(--nxt-size-unit) * 12);
  overflow-y: auto;
  padding: var(--nxt-space-2xs);
  border: var(--nxt-hairline) solid var(--nxt-popover-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-popover-bg);
  box-shadow: var(--nxt-popover-shadow);
  color: var(--nxt-popover-fg);
  font-size: var(--nxt-font-size-sm);
  user-select: none;
}
.nx-recipient-input-listbox.is-stale {
  opacity: 0.6;
}
.nx-recipient-input-option {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border-radius: var(--nxt-radius-sm);
  color: var(--nxt-menu-item-fg);
  cursor: pointer;
}
.nx-recipient-input-option.is-active {
  background: var(--nxt-menu-item-bg-active);
}
.nx-recipient-input-option > * {
  flex: none;
}
.nx-recipient-input-option-text {
  display: flex;
  flex: 0 1 auto;
  flex-direction: column;
  min-width: 0;
  line-height: var(--nxt-line-height-tight);
}
.nx-recipient-input-option-label,
.nx-recipient-input-option-email {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.nx-recipient-input-option-label {
  font-weight: var(--nxt-font-weight-medium);
}
.nx-recipient-input-option-email {
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
}
.nx-recipient-input-option-aside {
  margin-inline-start: auto;
  padding-inline-start: var(--nxt-space-xs);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
  white-space: nowrap;
}
.nx-recipient-input-message {
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  color: var(--nxt-fg-subtle);
}

.nx-recipient-input-error {
  margin: 0;
  /* The global type reset sizes every bare <p> at 16px medium. */
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-regular);
  color: var(--nxt-danger-subtle-fg);
}

/* Visually hidden, still read. */
.nx-recipient-input-live {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
</style>
